/* eslint-disable max-len */
import { TFunction } from 'i18next';
import React from 'react';

import TabSection from '../components/tab-section';

export type TermsSectionEntry = {
  tabLabel: string;
  component: React.FC;
};

const TAB_LABEL_KEYS = [
  'onboarding:terms-item-one',
  'onboarding:terms-item-two',
  'onboarding:terms-item-three',
  'onboarding:terms-item-four',
  'onboarding:terms-item-five',
  'onboarding:terms-item-six',
  'onboarding:terms-item-seven',
  'onboarding:terms-item-eight',
  'onboarding:terms-item-nine',
  'onboarding:terms-item-ten',
  'onboarding:terms-item-eleven',
  'onboarding:terms-item-twelve',
  'onboarding:terms-item-thirteen',
  'onboarding:terms-item-fourteen',
];

export const termsAndConditions = {
  titulo: 'Terms Of Service',
  secciones: [
    {
      titulo: '1. ACCEPTANCE OF TERMS AND PLATFORM OVERVIEW',
      secciones: [
        {
          subtitulo: '',
          contenido: `By accessing and using this Championship Title Web Application ("Platform" or "Service"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service, as well as our Privacy Policy and all applicable laws and regulations.`,
        },
        {
          subtitulo: '1.1 Platform Purpose',
          contenido: `This Platform is designed to facilitate and streamline real estate title workflows specifically within the State of Texas. The Platform provides tools and resources to assist users in managing title searches, preliminary title reports, deed recording information, chain of title examination, title insurance coordination, and other related title administration functions in accordance with Texas real estate title standards.`,
        },
        {
          subtitulo: '1.2 Services Provided',
          contenido: `The Platform enables users to:
- Maintain and organize title documents and records
- Track title examination workflows
- Manage preliminary title reports
- Coordinate with title service providers
- Document chain of title information
- Generate title-related reports and summaries`,
        },
      ],
    },
    {
      titulo: '2. NOT A SUBSTITUTE FOR LEGAL COUNSEL',
      secciones: [
        {
          subtitulo: '2.1 Disclaimer of Legal Advice',
          contenido: `THIS PLATFORM DOES NOT PROVIDE LEGAL ADVICE AND DOES NOT CONSTITUTE THE PRACTICE OF LAW. The Platform is a workflow management and informational tool only. Information, resources, and templates provided through the Platform should not be construed as legal advice, counsel, or professional legal guidance.`,
        },
        {
          subtitulo: '2.2 Attorney Consultation Required',
          contenido: `Users conducting real estate transactions in Texas should retain qualified Texas-licensed real estate attorneys or title companies to provide legal advice, conduct title examinations, issue title commitments, and represent their interests in transactions. The Platform does not replace the attorney-client relationship or the independent professional judgment of a licensed legal professional.`,
        },
        {
          subtitulo: '2.3 No Duty to Update',
          contenido: `While the Platform strives to reflect current Texas real estate title law and procedures, laws and regulations change frequently. The Platform makes no warranty that all information is current or complete. Users must verify all information with current statutes and regulations and consult with legal counsel before relying on any information in title transactions.`,
        },
      ],
    },
    {
      titulo: '3. USER RESPONSIBILITIES AND ACCEPTABLE USE',
      secciones: [
        {
          subtitulo: '3.1 User Eligibility',
          contenido: `You represent and warrant that:
- You are at least 18 years of age or otherwise capable of entering into binding contracts
- You have the legal authority to enter into this agreement
- You are a licensed real estate professional, title company employee, real estate attorney, or authorized representative of a party to a real estate transaction
- You will use the Platform only for lawful purposes related to Texas real estate title administration`,
        },
        {
          subtitulo: '3.2 Prohibited Uses',
          contenido: `You agree not to:
- Use the Platform for unlawful purposes or in violation of any applicable federal, state, or local laws
- Engage in fraudulent, deceptive, or misleading conduct
- Falsify, alter, or forge any documents or records within the Platform
- Misrepresent chain of title information or material facts affecting property title
- Use the Platform to facilitate transaction fraud, identity theft, or misappropriation of funds
- Violate Real Estate Settlement Procedures
- Interfere with or disrupt the Platform's operations or security
- Attempt to gain unauthorized access to Platform systems
- Reverse engineer, decompile, or attempt to discover source code
- Use automated tools, bots, or scrapers to access the Platform
- Share credentials with unauthorized third parties
- Create false or misleading property information`,
        },
        {
          subtitulo: '3.3 Professional Responsibility',
          contenido: `Users who are licensed attorneys or title professionals acknowledge their obligation to comply with all applicable professional standards, title industry standards, and ethical guidelines governing their respective professions.`,
        },
      ],
    },
    {
      titulo: '4. CONFIDENTIALITY AND DATA SECURITY',
      secciones: [
        {
          subtitulo: '4.1 Confidential Information',
          contenido: `The Platform may contain or allow upload of confidential real estate information, including:
- Personal identifying information of buyers, sellers, and lenders
- Title examination findings and exceptions
- Survey data and legal descriptions
- Lien and encumbrance information
- Financial terms and closing conditions

Users shall maintain strict confidentiality of all such information and comply with all applicable federal, state and local laws, ordinances, rules and regulations, and all duly promulgated orders and other duly authorized actions of any Governmental Authority having jurisdiction over the confidential information.`,
        },
        {
          subtitulo: '4.2 Data Security Measures',
          contenido: `We implement industry-standard security measures including:
- Encryption of data in transit (TLS/SSL) and at rest
- Secure authentication protocols
- Access controls and role-based permissions
- Audit logging of user activities`,
        },
        {
          subtitulo: '4.3 Limitation of Security',
          contenido: `While we maintain reasonable security measures, no system is absolutely secure. We do not warrant that unauthorized access, hacking, or data breaches will not occur. Users assume responsibility for protecting their account credentials and immediately report any suspected security breaches.`,
        },
        {
          subtitulo: '4.4 User Data Responsibility',
          contenido: `Users are responsible for:
- Maintaining backup copies of critical data
- Implementing their own security controls for sensitive information
- Complying with document retention requirements under Texas law and applicable regulations
- Following their organization's information security policies`,
        },
      ],
    },
    {
      titulo: '5. LIABILITY LIMITATIONS AND DISCLAIMERS',
      secciones: [
        {
          subtitulo: '5.1 Disclaimer of Warranties',
          contenido: `THE PLATFORM IS PROVIDED ON AN "AS-IS" AND "AS-AVAILABLE" BASIS WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING:
- Warranties of merchantability, fitness for a particular purpose, or non-infringement
- Warranties that the Platform will meet your requirements
- Warranties regarding accuracy, completeness, or timeliness of information
- Warranties that the Platform will be error-free or uninterrupted`,
        },
        {
          subtitulo: '5.2 Limitation of Liability',
          contenido: `TO THE MAXIMUM EXTENT PERMITTED BY TEXAS LAW, OUR TOTAL LIABILITY TO YOU SHALL NOT EXCEED THE FEES PAID BY YOU IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.

IN NO EVENT SHALL WE BE LIABLE FOR:
- Indirect, incidental, special, consequential, or punitive damages
- Lost profits, business interruption, or loss of data
- Costs of substitute services or goods
- Damages arising from title defects, chain of title issues, or transaction failures
- Damages related to reliance on Platform information

This limitation applies even if we have been advised of the possibility of such damages.`,
        },
        {
          subtitulo: '5.3 No Liability for Third-Party Content',
          contenido: `The Platform may contain links to or information from third parties, including government records, title company information, or other resources. We are not responsible for the accuracy, completeness, or legality of third-party content, and your use is at your sole risk.`,
        },
        {
          subtitulo: '5.4 User Assumption of Risk',
          contenido: `You assume all risks associated with:
- Errors or omissions in Platform data or information
- Reliance on Platform information in real estate transactions
- Use of the Platform in violation of these Terms
- Unauthorized access or modification of your account or data`,
        },
      ],
    },
    {
      titulo: '6. INTELLECTUAL PROPERTY RIGHTS',
      secciones: [
        {
          subtitulo: '6.1 Platform Ownership',
          contenido: `All content, features, and functionality of the Platform, including but not limited to:
- Software code and architecture
- User interface and design
- Databases and compilations
- Logos, trademarks, and service marks
- Templates and reports
- Proprietary systems and processes

are the exclusive property of Championship Title or our licensors and are protected by copyright, trademark, and other intellectual property laws.`,
        },
        {
          subtitulo: '6.2 Limited License',
          contenido: `We grant you a limited, non-exclusive, non-transferable license to use the Platform solely for authorized, lawful purposes. This license does not permit you to:
- Copy or reproduce Platform content for redistribution
- Create derivative works from the Platform
- Reverse engineer or decompile the Platform
- Resell or sublicense the Platform
- Remove copyright or proprietary notices`,
        },
        {
          subtitulo: '6.3 User Content',
          contenido: `Any documents, data, or information you upload to the Platform remains your property. By uploading content, you grant us a limited license to:
- Store and maintain your data on secure servers
- Process data as necessary to provide Platform services
- Use anonymized, aggregated data for service improvement
- Comply with legal process or law enforcement requests

You retain responsibility for ensuring your uploaded content does not infringe third-party intellectual property rights.`,
        },
        {
          subtitulo: '6.4 Feedback and Suggestions',
          contenido: `Any feedback, suggestions, or improvements you provide to us regarding the Platform may be used without restriction or compensation to you.`,
        },
      ],
    },
    {
      titulo: '7. USER ACCOUNTS, AUTHENTICATION, AND ACCESS CONTROLS',
      secciones: [
        {
          subtitulo: '7.1 Account Registration',
          contenido: `To use the Platform, you must:
- Create an account with accurate, current information
- Maintain the confidentiality of your password and username
- Implement strong password practices
- Immediately notify us of unauthorized account access`,
        },
        {
          subtitulo: '7.2 Account Responsibility',
          contenido: `You are responsible for all activities occurring under your account credentials. We are not responsible for unauthorized access resulting from your failure to protect credentials or your violation of these Terms.`,
        },
        {
          subtitulo: '7.3 Role-Based Access',
          contenido: `The Platform implements role-based access controls. Your access permissions depend on:
- Your user role (attorney, title professional, real estate agent, etc.)
- Your organization's subscription level
- Specific permissions assigned by your account administrator`,
        },
        {
          subtitulo: '7.4 Account Suspension and Termination',
          contenido: `We may suspend or terminate your account if:
- You violate these Terms of Service
- You violate applicable laws or regulations
- You engage in fraud, misrepresentation, or security threats
- You fail to pay required fees
- Your professional license is suspended or revoked
- We receive notice requiring account suspension`,
        },
      ],
    },
    {
      titulo: '8. DISPUTE RESOLUTION AND GOVERNING LAW',
      secciones: [
        {
          subtitulo: '8.1 Governing Law',
          contenido: `These Terms of Service and your use of the Platform are governed by and construed in accordance with the laws of the State of Texas, without regard to its conflicts of law principles.`,
        },
        {
          subtitulo: '8.2 Venue',
          contenido: `You irrevocably consent to the exclusive jurisdiction and venue of the state and federal courts located in Texas for any legal proceedings arising from or relating to these Terms or your use of the Platform.`,
        },
        {
          subtitulo: '8.3 Arbitration Clause',
          contenido: `EITHER PARTY MAY ELECT TO RESOLVE DISPUTES THROUGH BINDING ARBITRATION RATHER THAN LITIGATION.

If arbitration is elected:
- Disputes shall be resolved by a single arbitrator in accordance with JAMS or AAA Commercial Arbitration Rules
- The arbitration shall be conducted in Texas
- Each party bears its own attorney's fees unless the arbitrator awards fees as permitted by law
- The arbitrator's decision shall be final and binding`,
        },
        {
          subtitulo: '8.4 Exceptions to Arbitration',
          contenido: `Notwithstanding the arbitration clause, the following matters may be brought in court without arbitration:
- Claims for injunctive relief to prevent intellectual property infringement
- Claims arising from account security breaches
- Regulatory or compliance investigations`,
        },
        {
          subtitulo: '8.5 Class Action Waiver',
          contenido: `YOU WAIVE YOUR RIGHT TO PARTICIPATE IN CLASS ACTIONS, CLASS ARBITRATIONS, OR REPRESENTATIVE ACTIONS AGAINST US. ALL PROCEEDINGS SHALL BE CONDUCTED ON AN INDIVIDUAL BASIS.`,
        },
        {
          subtitulo: '8.6 Statute of Limitations',
          contenido: `Any claim arising from these Terms must be filed within two (2) years of when the claim arose or be forever barred.`,
        },
      ],
    },
    {
      titulo: '9. MODIFICATIONS TO TERMS OF SERVICE',
      secciones: [
        {
          subtitulo: '9.1 Right to Modify',
          contenido: `We reserve the right to modify these Terms of Service at any time. Modifications become effective upon posting to the Platform or notification to your registered email address.`,
        },
        {
          subtitulo: '9.2 Continued Use After Modification',
          contenido: `Your continued use of the Platform following notice of modifications constitutes acceptance of the modified Terms. If you do not agree with modifications, you must discontinue use of the Platform and request account termination.`,
        },
        {
          subtitulo: '9.3 Material Changes',
          contenido: `For material changes that adversely affect your rights or obligations, we will provide at least thirty (30) days' notice before the changes become effective. Your continued use after the notice period constitutes acceptance.`,
        },
        {
          subtitulo: '9.4 Notice of Changes',
          contenido: `We will notify you of material changes through:
- Email to your registered account address
- Pop-up notice upon next login`,
        },
      ],
    },
    {
      titulo: '10. TERMINATION OF SERVICE',
      secciones: [
        {
          subtitulo: '10.1 Termination by User',
          contenido: `You may terminate your account at any time by providing written notice using our contact information below.`,
        },
        {
          subtitulo: '10.2 Termination by Platform',
          contenido: `We may terminate service if:
- Your account has been inactive for twelve (12) months
- You violate these Terms of Service
- You violate applicable law or regulations
- Your professional license is suspended or revoked
- We cease offering the Platform or specific services
- Payment for subscription services fails or is overdue`,
        },
        {
          subtitulo: '10.3 Effects of Termination',
          contenido: `Upon termination:
- Your access to the Platform terminates immediately
- You remain responsible for outstanding fees and liabilities
- We may delete your account data after a reasonable retention period (typically ninety (90) days)
- Provisions that survive termination include liability limitations, confidentiality obligations, and dispute resolution clauses`,
        },
        {
          subtitulo: '10.4 Data Recovery After Termination',
          contenido: `You are responsible for maintaining backup copies of your data. We are not obligated to provide data after account termination, though we may provide reasonable access during a transition period upon request.`,
        },
      ],
    },
    {
      titulo: '11. MANDATORY DISCLOSURES FOR TEXAS REAL ESTATE TITLE SERVICES',
      secciones: [
        {
          subtitulo: '11.1 Title Company and Insurance Notice',
          contenido: `If you are a title company or insurance provider using this Platform:
- You must maintain required Texas Insurance Department licenses and certifications
- You must comply with all Texas Insurance Code provisions
- Disclosures required under Texas Insurance Code must be provided separately by you, not through this Platform
- Title commitment terms and conditions must be clearly communicated to clients`,
        },
        {
          subtitulo: '11.2 Attorney Responsibility',
          contenido: `Licensed attorneys using this Platform acknowledge:
- You remain responsible for the quality and accuracy of title work
- This Platform does not substitute for independent legal research
- You must comply with the Texas Rules of Professional Conduct
- Conflicts of interest and ethical obligations remain your responsibility
- Client confidentiality obligations are not satisfied by Platform confidentiality measures`,
        },
        {
          subtitulo: '11.3 Real Estate Agent Disclosures',
          contenido: `Real estate agents or brokers using this Platform must:
- Comply with Texas Real Estate Commission (TREC) regulations
- Maintain required TREC disclosures separately from Platform documents
- Ensure TREC-required information is properly delivered to parties
- Not represent Platform services as legal or title insurance`,
        },
        {
          subtitulo: '11.4 Transaction Document Retention',
          contenido: `Users must maintain transaction documents and records in accordance with:
- Texas Property Code (chain of title record retention)
- Federal requirement for TRID records (three (3) years)
- Any lender or investor requirements (typically seven (7) years)
- The Platform is not a substitute for proper record retention systems`,
        },
      ],
    },
    {
      titulo: '12. CONTACT AND SUPPORT',
      secciones: [
        {
          subtitulo: '',
          contenido: `For questions regarding these Terms of Service, account issues, or to report violations:

Austin Office
- Mailing Address: 8000 Centre Park Dr. Ste. 220 Austin, TX 78754
- Phone: (512) 982-6116

McAllen Office
- Mailing Address: 917 East Esperanza Ave McAllen, TX 78501
- Phone: (956) 688-9829

Rockport Fulton Office
- Mailing Address: 2902 Traylor Blvd, Ste. 207 Rockport, TX 78382
- Phone: (361) 502-5005`,
        },
      ],
    },
    {
      titulo: '13. SEVERABILITY',
      secciones: [
        {
          subtitulo: '',
          contenido: `If any provision of these Terms of Service is found to be invalid or unenforceable by a court of competent jurisdiction, such provision shall be severable and the remaining provisions shall continue in full force and effect to the maximum extent permitted by Texas law.`,
        },
      ]
    },
    {
      titulo: '14. ENTIRE AGREEMENT',
      secciones: [
        {
          subtitulo: '',
          contenido: `These Terms of Service, together with our Privacy Policy and any other policies referenced herein, constitute the entire agreement between you and us regarding your use of the Platform and supersede all prior negotiations, understandings, and agreements.`
        },
      ]
    }
  ],
};

export const getTermsSections = (t: TFunction): Record<string, TermsSectionEntry> => {
  const sections: Record<string, TermsSectionEntry> = {};

  termsAndConditions.secciones.forEach((seccion, index) => {
    const sectionKey = String(index + 1);
    const data = seccion;

    sections[sectionKey] = {
      tabLabel: t(TAB_LABEL_KEYS[index]),
      component: () => React.createElement(TabSection, { data }),
    };
  });

  return sections;
};
